import { LuEllipsisVertical } from '@react-icons/all-files/lu/LuEllipsisVertical'
import * as React from 'react'
import { useMediaQuery } from 'react-responsive'
import { useLocation, useNavigate } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { getSelectedLibrary } from '@disco/state-management/selectors/app'
import { getShortlistByPlaylistId } from '@disco/state-management/selectors/shortlists'

import type { PlaylistId } from 'types/playlist'

import { getShortlistBaseRoute } from 'utils/routing'

import { DropdownMenu, IconButton } from 'components/ds'
import ActionButton from 'components/ui/action-button'

import { useSelector } from 'hooks/use-redux'

import PlaylistDropdown from './playlist-dropdown'

type Props = {
  buttonSize?: 'default' | 'compact'
  playlist:
    | {
        playlist_id: PlaylistId
      }
    | null
    | undefined
  mixpanelContext?:
    | 'Explorer'
    | 'Playlist pane'
    | 'Shortlist'
    | 'Browse'
    | 'Starred'
    | 'Inbox'
    | 'Channel'
    | 'Search results'
  hideShare?: boolean
  hideShareShortcut?: boolean
}

const PlaylistActions = ({
  buttonSize,
  playlist,
  mixpanelContext,
  hideShare,
  hideShareShortcut,
}: Props): React.ReactNode => {
  const navigate = useNavigate()
  const location = useLocation()
  const mobile = useMediaQuery({ maxWidth: 639 })
  const selectedLibrary = useSelector(getSelectedLibrary)
  const [pendingPlaylistSettings, setPendingPlaylistSettings] =
    React.useState(false)
  const shortlist = useSelector(state =>
    playlist ? getShortlistByPlaylistId(state, playlist.playlist_id) : null,
  )

  // Navigate to the playlist settings save modal once the shortlist is ready.
  // This handles the case where the user clicks "Playlist Settings" before any
  // shortlist exists for the playlist — the dropdown unmounts on close so we
  // can't rely on an effect inside PlaylistSettings itself.
  React.useEffect(() => {
    if (!pendingPlaylistSettings || !shortlist || !shortlist.items?.length)
      return
    const baseRoute = getShortlistBaseRoute(location)
    navigate(`${baseRoute}/shortlists/${shortlist.id}/save`, {
      state: { location: 'playlist_settings' },
    })
    setPendingPlaylistSettings(false)
  }, [pendingPlaylistSettings, shortlist, navigate, location])

  if (!playlist) return null

  const onShortcutClick = () => {
    mixpanel.sendPureEvent('Click icon', {
      Type: 'Playlist',
      'Playlist ID': playlist.playlist_id,
      Icon: 'Share',
      Context: mixpanelContext,
    })
    navigate(`/explorer/playlists/${playlist.playlist_id}/share`)
  }

  return (
    <>
      {!hideShare && !hideShareShortcut && !mobile && (
        <ActionButton compact icon="share-arrow" onClick={onShortcutClick} />
      )}
      <DropdownMenu.Root
        onOpenChange={open => {
          if (open) {
            mixpanel.sendPureEvent('Click icon', {
              'Playlist ID': playlist.playlist_id,
              Type: 'Playlist',
              Icon: 'Context menu',
              Context: mixpanelContext,
            })
          }
        }}
      >
        <DropdownMenu.Trigger>
          <IconButton
            variant="ghost"
            color="gray"
            highContrast
            size={buttonSize === 'compact' ? '1' : '2'}
            aria-label="More actions"
          >
            <LuEllipsisVertical size={20} />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          <PlaylistDropdown
            group={selectedLibrary}
            playlist={playlist}
            addToCustomGroup
            addToShortlist
            deletePlaylist
            downloadZip
            editMetadata
            editPlaylist
            playlistStats
            sharePlaylist={!hideShare}
            trackOrder
            onPlaylistSettingsAwait={() => setPendingPlaylistSettings(true)}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </>
  )
}

export default PlaylistActions
